// frontend/src/components/ProtectedRoute.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole, canAccessRoute } from '../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Verificar roles si se especificaron
  if (requiredRoles && user) {
    const userRole = user.rol_usr as UserRole;
    if (!requiredRoles.includes(userRole)) {
      // Redirigir según el rol del usuario
      switch (userRole) {
        case UserRole.ADMIN:
          return <Navigate to="/dashboard" replace />;
        case UserRole.VENDEDORA:
        case UserRole.VENDEDORA_EN_LINEA:
          return <Navigate to="/ventas" replace />;
        case UserRole.MODISTA:
          return <Navigate to="/tickets" replace />;
        default:
          return <Navigate to="/login" replace />;
      }
    }
  }

  // Verificar que el usuario tenga acceso a esta ruta según su rol
  if (user) {
    const userRole = user.rol_usr as UserRole;
    const currentPath = location.pathname;

    // Verificar permisos específicos por ruta
    if (!canAccessRoute(userRole, currentPath)) {
      // Si no tiene permisos, redirigir a la página principal según su rol
      switch (userRole) {
        case UserRole.ADMIN:
          if (currentPath !== '/dashboard') {
            return <Navigate to="/dashboard" replace />;
          }
          break;
        case UserRole.VENDEDORA:
        case UserRole.VENDEDORA_EN_LINEA:
          if (!['/ventas', '/inventario', '/tickets', '/clientes'].includes(currentPath)) {
            return <Navigate to="/ventas" replace />;
          }
          break;
        case UserRole.MODISTA:
          if (!['/tickets', '/calendario'].includes(currentPath)) {
            return <Navigate to="/tickets" replace />;
          }
          break;
      }
    }
  }

  return <>{children}</>;
};
